import type { FormGroup } from '@angular/forms';
import type {
  WaterContentBlockSaveRequest,
  WaterExpectationSaveRequest,
  WaterHotspotSaveRequest,
  WaterInformationDetail,
  WaterInformationSaveRequest,
  WaterPhoto,
  WaterPriceLabelSaveRequest,
} from '@models/water-information';
import type {
  GeneralForm,
  HotspotIntroductionForm,
  ImageState,
  LocationForm,
  PricingForm,
  SeoForm,
} from './water-information-form.types';

export interface FormGroups {
  general: FormGroup<GeneralForm>;
  pricing: FormGroup<PricingForm>;
  location: FormGroup<LocationForm>;
  seo: FormGroup<SeoForm>;
  hotspotIntroduction: FormGroup<HotspotIntroductionForm>;
}

export interface ImageStateMap {
  appHeader: ImageState;
  mapImage: ImageState;
  locationImage: ImageState;
  ambiancePoster: ImageState;
}

export interface CollectionState {
  photos: WaterPhoto[];
  expectations: WaterExpectationSaveRequest[];
  hotspots: WaterHotspotSaveRequest[];
  contentBlocks: WaterContentBlockSaveRequest[];
  priceLabels: WaterPriceLabelSaveRequest[];
}

export function patchForms(forms: FormGroups, info: WaterInformationDetail): void {
  forms.general.patchValue({
    shortDescriptionNl: info.shortDescriptionNl,
    shortDescriptionEn: info.shortDescriptionEn,
    shortDescriptionDe: info.shortDescriptionDe,
    shortDescriptionIt: info.shortDescriptionIt,
    shortDescriptionFr: info.shortDescriptionFr,
    placeNl: info.placeNl,
    placeEn: info.placeEn,
    placeDe: info.placeDe,
    placeIt: info.placeIt,
    placeFr: info.placeFr,
    ambianceVideoNl: info.ambianceVideoNl,
    ambianceVideoEn: info.ambianceVideoEn,
    ambianceVideoDe: info.ambianceVideoDe,
    ambianceVideoIt: info.ambianceVideoIt,
    ambianceVideoFr: info.ambianceVideoFr,
    uspsNl: info.uspsNl,
    uspsEn: info.uspsEn,
    uspsDe: info.uspsDe,
    uspsIt: info.uspsIt,
    uspsFr: info.uspsFr,
    rulesNl: info.rulesNl,
    rulesEn: info.rulesEn,
    rulesDe: info.rulesDe,
    rulesIt: info.rulesIt,
    rulesFr: info.rulesFr,
    infoPackNl: info.infoPackNl,
    infoPackEn: info.infoPackEn,
    infoPackDe: info.infoPackDe,
    infoPackIt: info.infoPackIt,
    infoPackFr: info.infoPackFr,
  });

  forms.pricing.patchValue({
    priceIntroductionNl: info.priceIntroductionNl,
    priceIntroductionEn: info.priceIntroductionEn,
    priceIntroductionDe: info.priceIntroductionDe,
    priceIntroductionIt: info.priceIntroductionIt,
    priceIntroductionFr: info.priceIntroductionFr,
    plainPriceSummaryNl: info.plainPriceSummaryNl,
    plainPriceSummaryEn: info.plainPriceSummaryEn,
    plainPriceSummaryDe: info.plainPriceSummaryDe,
    plainPriceSummaryIt: info.plainPriceSummaryIt,
    plainPriceSummaryFr: info.plainPriceSummaryFr,
    plainPriceServicesTextNl: info.plainPriceServicesTextNl,
    plainPriceServicesTextEn: info.plainPriceServicesTextEn,
    plainPriceServicesTextDe: info.plainPriceServicesTextDe,
    plainPriceServicesTextIt: info.plainPriceServicesTextIt,
    plainPriceServicesTextFr: info.plainPriceServicesTextFr,
  });

  forms.location.patchValue({
    locationAddressNl: info.locationAddressNl,
    locationAddressEn: info.locationAddressEn,
    locationAddressDe: info.locationAddressDe,
    locationAddressIt: info.locationAddressIt,
    locationAddressFr: info.locationAddressFr,
    locationCoordinates: info.locationCoordinates,
  });

  forms.seo.patchValue({
    metaDescriptionNl: info.metaDescriptionNl,
    metaDescriptionEn: info.metaDescriptionEn,
    metaDescriptionDe: info.metaDescriptionDe,
    metaDescriptionIt: info.metaDescriptionIt,
    metaDescriptionFr: info.metaDescriptionFr,
  });

  forms.hotspotIntroduction.patchValue({
    nl: info.hotspotIntroductionNl,
    en: info.hotspotIntroductionEn,
    de: info.hotspotIntroductionDe,
    it: info.hotspotIntroductionIt,
    fr: info.hotspotIntroductionFr,
  });
}

export function toImageStateMap(info: WaterInformationDetail): ImageStateMap {
  return {
    appHeader: { name: info.appHeaderPhotoName, url: info.appHeaderPhotoUrl },
    mapImage: { name: info.mapImageName, url: info.mapImageUrl },
    locationImage: { name: info.locationImageName, url: info.locationImageUrl },
    ambiancePoster: { name: info.ambianceVideoImageName, url: info.ambianceVideoImageUrl },
  };
}

export function toCollections(info: WaterInformationDetail): CollectionState {
  return {
    photos: info.photos,
    expectations: info.expectations.map((e) => ({
      id: e.id,
      available: e.available,
      textNl: e.textNl,
      textEn: e.textEn,
      textDe: e.textDe,
      textIt: e.textIt,
      textFr: e.textFr,
    })),
    hotspots: info.hotspots.map((h) => ({
      id: h.id,
      clientId: h.clientId,
      city: h.city,
      distanceToWater: h.distanceToWater,
      textNl: h.textNl,
      textEn: h.textEn,
      textDe: h.textDe,
      textIt: h.textIt,
      textFr: h.textFr,
      imageName: h.imageName,
      imageUrl: h.imageUrl,
    })),
    contentBlocks: info.contentBlocks.map((b) => ({
      id: b.id,
      clientId: b.clientId,
      type: b.type,
      anchorNl: b.anchorNl,
      anchorEn: b.anchorEn,
      anchorDe: b.anchorDe,
      anchorIt: b.anchorIt,
      anchorFr: b.anchorFr,
      titleNl: b.titleNl,
      titleEn: b.titleEn,
      titleDe: b.titleDe,
      titleIt: b.titleIt,
      titleFr: b.titleFr,
      textNl: b.textNl,
      textEn: b.textEn,
      textDe: b.textDe,
      textIt: b.textIt,
      textFr: b.textFr,
      videoNl: b.videoNl,
      videoEn: b.videoEn,
      videoDe: b.videoDe,
      videoIt: b.videoIt,
      videoFr: b.videoFr,
      iframeTitleNl: b.iframeTitleNl,
      iframeTitleEn: b.iframeTitleEn,
      iframeTitleDe: b.iframeTitleDe,
      iframeTitleIt: b.iframeTitleIt,
      iframeTitleFr: b.iframeTitleFr,
      iframeTextNl: b.iframeTextNl,
      iframeTextEn: b.iframeTextEn,
      iframeTextDe: b.iframeTextDe,
      iframeTextIt: b.iframeTextIt,
      iframeTextFr: b.iframeTextFr,
      iframeNl: b.iframeNl,
      iframeEn: b.iframeEn,
      iframeDe: b.iframeDe,
      iframeIt: b.iframeIt,
      iframeFr: b.iframeFr,
      imageName: b.imageName,
      imageUrl: b.imageUrl,
    })),
    priceLabels: info.priceLabels.map((p) => ({
      id: p.id,
      labelNl: p.labelNl,
      labelEn: p.labelEn,
      labelDe: p.labelDe,
      labelIt: p.labelIt,
      labelFr: p.labelFr,
      titleNl: p.titleNl,
      titleEn: p.titleEn,
      titleDe: p.titleDe,
      titleIt: p.titleIt,
      titleFr: p.titleFr,
      subTitleNl: p.subTitleNl,
      subTitleEn: p.subTitleEn,
      subTitleDe: p.subTitleDe,
      subTitleIt: p.subTitleIt,
      subTitleFr: p.subTitleFr,
      tablesNl: p.tablesNl,
      tablesEn: p.tablesEn,
      tablesDe: p.tablesDe,
      tablesIt: p.tablesIt,
      tablesFr: p.tablesFr,
    })),
  };
}

function filterEmptyStrings(arr: string[] | null | undefined): string[] | null {
  if (!arr) return null;
  const filtered = arr.filter((s) => s.trim() !== '');
  return filtered.length > 0 ? filtered : null;
}

export function toSaveDto(forms: FormGroups, collections: CollectionState): WaterInformationSaveRequest {
  const g = forms.general.getRawValue();
  const p = forms.pricing.getRawValue();
  const l = forms.location.getRawValue();
  const s = forms.seo.getRawValue();
  const h = forms.hotspotIntroduction.getRawValue();

  return {
    shortDescriptionNl: g.shortDescriptionNl || null,
    shortDescriptionEn: g.shortDescriptionEn || null,
    shortDescriptionDe: g.shortDescriptionDe || null,
    shortDescriptionIt: g.shortDescriptionIt || null,
    shortDescriptionFr: g.shortDescriptionFr || null,
    placeNl: g.placeNl || null,
    placeEn: g.placeEn || null,
    placeDe: g.placeDe || null,
    placeIt: g.placeIt || null,
    placeFr: g.placeFr || null,
    ambianceVideoNl: g.ambianceVideoNl || null,
    ambianceVideoEn: g.ambianceVideoEn || null,
    ambianceVideoDe: g.ambianceVideoDe || null,
    ambianceVideoIt: g.ambianceVideoIt || null,
    ambianceVideoFr: g.ambianceVideoFr || null,
    uspsNl: filterEmptyStrings(g.uspsNl),
    uspsEn: filterEmptyStrings(g.uspsEn),
    uspsDe: filterEmptyStrings(g.uspsDe),
    uspsIt: filterEmptyStrings(g.uspsIt),
    uspsFr: filterEmptyStrings(g.uspsFr),
    rulesNl: filterEmptyStrings(g.rulesNl),
    rulesEn: filterEmptyStrings(g.rulesEn),
    rulesDe: filterEmptyStrings(g.rulesDe),
    rulesIt: filterEmptyStrings(g.rulesIt),
    rulesFr: filterEmptyStrings(g.rulesFr),
    infoPackNl: filterEmptyStrings(g.infoPackNl),
    infoPackEn: filterEmptyStrings(g.infoPackEn),
    infoPackDe: filterEmptyStrings(g.infoPackDe),
    infoPackIt: filterEmptyStrings(g.infoPackIt),
    infoPackFr: filterEmptyStrings(g.infoPackFr),
    priceIntroductionNl: p.priceIntroductionNl || null,
    priceIntroductionEn: p.priceIntroductionEn || null,
    priceIntroductionDe: p.priceIntroductionDe || null,
    priceIntroductionIt: p.priceIntroductionIt || null,
    priceIntroductionFr: p.priceIntroductionFr || null,
    plainPriceSummaryNl: p.plainPriceSummaryNl || null,
    plainPriceSummaryEn: p.plainPriceSummaryEn || null,
    plainPriceSummaryDe: p.plainPriceSummaryDe || null,
    plainPriceSummaryIt: p.plainPriceSummaryIt || null,
    plainPriceSummaryFr: p.plainPriceSummaryFr || null,
    plainPriceServicesTextNl: p.plainPriceServicesTextNl || null,
    plainPriceServicesTextEn: p.plainPriceServicesTextEn || null,
    plainPriceServicesTextDe: p.plainPriceServicesTextDe || null,
    plainPriceServicesTextIt: p.plainPriceServicesTextIt || null,
    plainPriceServicesTextFr: p.plainPriceServicesTextFr || null,
    locationAddressNl: l.locationAddressNl || null,
    locationAddressEn: l.locationAddressEn || null,
    locationAddressDe: l.locationAddressDe || null,
    locationAddressIt: l.locationAddressIt || null,
    locationAddressFr: l.locationAddressFr || null,
    locationCoordinates: l.locationCoordinates || null,
    metaDescriptionNl: s.metaDescriptionNl || null,
    metaDescriptionEn: s.metaDescriptionEn || null,
    metaDescriptionDe: s.metaDescriptionDe || null,
    metaDescriptionIt: s.metaDescriptionIt || null,
    metaDescriptionFr: s.metaDescriptionFr || null,
    hotspotIntroductionNl: h.nl || null,
    hotspotIntroductionEn: h.en || null,
    hotspotIntroductionDe: h.de || null,
    hotspotIntroductionIt: h.it || null,
    hotspotIntroductionFr: h.fr || null,
    photos: collections.photos.map((ph) => ({ id: ph.id, photoName: ph.photoName ?? '', order: ph.order })),
    expectations: collections.expectations,
    hotspots: collections.hotspots,
    contentBlocks: collections.contentBlocks,
    priceLabels: collections.priceLabels,
  };
}
